import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MemberRegistrationStepClient, MemberRegistrationStepDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new MemberRegistrationStepClient(API_BASE_URL, axiosInstance);

// Cached query for dropdowns
export const useMemberRegistrationSteps = () => {
    return useQuery({
        queryKey: ['memberRegistrationSteps'],
        queryFn: async () => {
            const response = await apiClient.searchMemberRegistrationSteps({
                page: 1,
                pageSize: MAX_DROPDOWN_PAGE_SIZE,
                sortBy: 'DisplayPosition',
            });
            return response?.data?.results || [];
        },
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        refetchOnReconnect: true,
    });
};

// Uncached search for admin tables
export const useMemberRegistrationStepSearch = () => {
    const search = async (params: any) => {
        const response = await apiClient.searchMemberRegistrationSteps(params);
        return {
            results: response?.data?.results || [],
            totalCount: response?.data?.totalCount || 0,
        };
    };

    return { search };
};

// Query by ID
export const useMemberRegistrationStepById = (id: string) => {
    return useQuery({
        queryKey: ['memberRegistrationStep', id],
        queryFn: async () => {
            const response = await apiClient.getMemberRegistrationStepById(id);
            return response?.data || null;
        },
        enabled: !!id,
    });
};

// Create mutation
export const useCreateMemberRegistrationStep = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: MemberRegistrationStepDTO) => {
            return await apiClient.createMemberRegistrationStep(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberRegistrationSteps'] });
        },
    });
};

// Update mutation
export const useUpdateMemberRegistrationStep = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: MemberRegistrationStepDTO }) => {
            return await apiClient.updateMemberRegistrationStep(id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberRegistrationSteps'] });
        },
    });
};

// Delete mutation
export const useDeleteMemberRegistrationStep = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            return await apiClient.deleteMemberRegistrationStep(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberRegistrationSteps'] });
        },
    });
};